import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Search, ChevronUp, ChevronDown, Printer, Download, ListTree, X } from "lucide-react";

export async function imprimirPdf(url) {
  const res = await api.get(url, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;width:0;height:0;border:0;right:0;bottom:0";
  f.src = blobUrl;
  f.onload = () => {
    try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(blobUrl, "_blank"); }
  };
  document.body.appendChild(f);
  setTimeout(() => f.remove(), 120000);
}

function membreteEnDoc(doc, m, logo) {
  const prim = m?.color_primario || "#9e1535";
  const sec = m?.color_secundario || "#e8b4c4";
  const head = doc.createElement("div");
  head.style.cssText = "max-width:980px;margin:0 auto;padding:18px 28px 0;position:relative";
  head.innerHTML = `<div style="position:absolute;right:0;top:0;width:160px;height:18px;background:${prim};clip-path:polygon(0 0,100% 0,100% 100%)"></div><img alt="Human Resources" style="height:48px"/><div style="border-top:1px solid ${sec};margin-top:14px"></div><div style="border-top:2px solid ${prim};margin-top:3px"></div>`;
  if (logo) head.querySelector("img").src = logo; else head.querySelector("img").remove();
  const pie = doc.createElement("div");
  pie.style.cssText = `max-width:924px;margin:24px auto 28px;border-top:1px solid ${sec};padding-top:8px;text-align:center;font:12px Arial,sans-serif;color:#333`;
  const l1 = doc.createElement("div"); l1.textContent = m?.direccion || "";
  const l2 = doc.createElement("div"); l2.textContent = `Teléfono: ${m?.telefono || ""}  |  Correo: ${m?.correo || ""}`;
  pie.append(l1, l2);
  doc.body.prepend(head);
  doc.body.append(pie);
}

export const fechaLarga = (f) => (f ? new Date(`${f.slice(0, 10)}T12:00:00`).toLocaleDateString("es-CO", { dateStyle: "long" }) : "—");
export const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }) : "—");

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const ESTILO = "mark[data-rit]{background:#fde68a;color:inherit;border-radius:2px}mark[data-rit].actual{background:#f59e0b;outline:2px solid #b45309}h1,h2,h3{scroll-margin-top:16px}";
const btn = "flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50";

function limpiar(doc) {
  doc.querySelectorAll("mark[data-rit]").forEach((m) => m.replaceWith(doc.createTextNode(m.textContent)));
  doc.body.normalize();
}

function marcar(doc, q) {
  limpiar(doc);
  const nq = norm(q.trim());
  if (nq.length < 2) return [];
  const walker = doc.createTreeWalker(doc.body, 4);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  const marks = [];
  nodes.forEach((n) => {
    const t = n.nodeValue;
    const nt = norm(t).length === t.length ? norm(t) : t.toLowerCase();
    let i = nt.indexOf(nq);
    if (i < 0) return;
    const frag = doc.createDocumentFragment();
    let last = 0;
    while (i >= 0) {
      frag.appendChild(doc.createTextNode(t.slice(last, i)));
      const m = doc.createElement("mark");
      m.setAttribute("data-rit", "");
      m.textContent = t.slice(i, i + nq.length);
      frag.appendChild(m);
      marks.push(m);
      last = i + nq.length;
      i = nt.indexOf(nq, last);
    }
    frag.appendChild(doc.createTextNode(t.slice(last)));
    n.replaceWith(frag);
  });
  return marks;
}

function useBusqueda(docRef, ready) {
  const [q, setQ] = useState("");
  const [marks, setMarks] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!ready || !docRef.current) return;
    const t = setTimeout(() => { setMarks(marcar(docRef.current, q)); setIdx(0); }, 250);
    return () => clearTimeout(t);
  }, [q, ready, docRef]);
  useEffect(() => {
    marks.forEach((m, i) => m.classList.toggle("actual", i === idx));
    marks[idx]?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [marks, idx]);
  const mover = (d) => marks.length && setIdx((i) => (i + d + marks.length) % marks.length);
  return { q, setQ, total: marks.length, idx, mover };
}

function Buscador({ b }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 focus-within:border-vino-400">
      <Search className="h-4 w-4 shrink-0 text-slate-400" />
      <input value={b.q} onChange={(e) => b.setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && b.mover(e.shiftKey ? -1 : 1)}
        placeholder="Buscar palabras en el reglamento" data-testid="rit-buscar-input" className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
      {b.q && <span className="shrink-0 text-xs text-slate-500" data-testid="rit-buscar-total">{b.total ? `${b.idx + 1} de ${b.total}` : "Sin resultados"}</span>}
      <button onClick={() => b.mover(-1)} disabled={!b.total} data-testid="rit-buscar-anterior" aria-label="Anterior" className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronUp className="h-4 w-4" /></button>
      <button onClick={() => b.mover(1)} disabled={!b.total} data-testid="rit-buscar-siguiente" aria-label="Siguiente" className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronDown className="h-4 w-4" /></button>
      {b.q && <button onClick={() => b.setQ("")} data-testid="rit-buscar-limpiar" aria-label="Limpiar" className="rounded p-0.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>}
    </div>
  );
}

function Capitulos({ caps, onIr }) {
  if (!caps.length) return null;
  return (
    <label className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm sm:max-w-xs">
      <ListTree className="h-4 w-4 shrink-0 text-slate-400" />
      <select value="" onChange={(e) => onIr(Number(e.target.value))} data-testid="rit-capitulos-select" className="min-w-0 flex-1 bg-transparent outline-none">
        <option value="" disabled>Ir a un capítulo…</option>
        {caps.map((c, i) => <option key={i} value={i}>{c}</option>)}
      </select>
    </label>
  );
}

export default function ReglamentoVisor({ html, membrete, onDescargar, descargando, onImprimir }) {
  const frame = useRef(null);
  const docRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [caps, setCaps] = useState([]);
  const [logo, setLogo] = useState(undefined);
  const [imprimiendo, setImprimiendo] = useState(false);
  const b = useBusqueda(docRef, ready);
  useEffect(() => {
    api.get("/reglamento/membrete/logo", { responseType: "blob" }).then((r) => setLogo(URL.createObjectURL(r.data))).catch(() => setLogo(null));
  }, []);

  const imprimir = async () => {
    setImprimiendo(true);
    try { await onImprimir(); } finally { setImprimiendo(false); }
  };
  const onLoad = () => {
    const doc = frame.current?.contentDocument;
    if (!doc?.body) return;
    const st = doc.createElement("style");
    st.textContent = ESTILO;
    doc.head.appendChild(st);
    membreteEnDoc(doc, membrete, logo);
    docRef.current = doc;
    let hs = [...doc.querySelectorAll("h2")];
    if (!hs.length) hs = [...doc.querySelectorAll("h1, h3")];
    setCaps(hs.map((h) => h.textContent.trim()));
    setReady(true);
  };
  const irA = (i) => {
    const doc = docRef.current;
    let hs = [...doc.querySelectorAll("h2")];
    if (!hs.length) hs = [...doc.querySelectorAll("h1, h3")];
    hs[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50" data-testid="rit-visor">
      <div className="flex flex-col gap-2 border-b border-slate-200 p-3 sm:flex-row sm:items-center">
        <Buscador b={b} />
        <Capitulos caps={caps} onIr={irA} />
        <div className="flex gap-2">
          <button onClick={imprimir} disabled={!ready || imprimiendo} data-testid="rit-imprimir-btn" className={btn}><Printer className="h-4 w-4" /> {imprimiendo ? "Preparando…" : "Imprimir"}</button>
          <button onClick={onDescargar} disabled={descargando} data-testid="rit-descargar-pdf-btn" className={btn}><Download className="h-4 w-4" /> {descargando ? "Preparando…" : "Descargar PDF"}</button>
        </div>
      </div>
      {logo !== undefined && <iframe ref={frame} title="Reglamento Interno de Trabajo" srcDoc={html} onLoad={onLoad} sandbox="allow-same-origin allow-modals"
        data-testid="rit-documento" className="block h-[72vh] w-full bg-white" />}
    </div>
  );
}
